<script lang="ts">
  /**
   * The "⋯" task actions menu for board cards.
   *
   * Mirrors the menu in ProjectRail (priority, duplicate, won't do, delete, move, convert)
   * but is rendered by the canvas rather than inside a card, and positioned with
   * Floating UI — see the BoardTaskUi docs in lib/boardContext.ts for why.
   *
   * "Move to project…" drills into a project search inside this same menu rather
   * than opening a second floating picker.
   */
  import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
  import type { Task, TaskPriority } from '../../lib/types';
  import Icon from '../Icon.svelte';
  import TaskCopyActionRow from '../TaskCopyActionRow.svelte';
  import MoveToProjectPanel from '../MoveToProjectPanel.svelte';
  import RecentMoveMenuItem from '../RecentMoveMenuItem.svelte';

  interface Props {
    task: Task | null;
    anchorEl: HTMLElement | null;
    onSetPriority: (task: Task, priority: TaskPriority) => void;
    onDuplicate: (task: Task) => void;
    onMarkWontDo: (task: Task) => void;
    onDelete: (task: Task) => void;
    /** Move the task to a project — picked from the in-menu search or the recent-move shortcut. */
    onMoveToProject: (task: Task, projectId: string, projectTitle: string) => void;
    onConvertToProject: (task: Task) => void;
    onClose: () => void;
    /** The project the anchored task belongs to — hides the shortcut when it's already there. */
    currentProjectId: string | null;
  }

  let {
    task,
    anchorEl,
    onSetPriority,
    onDuplicate,
    onMarkWontDo,
    onDelete,
    onMoveToProject,
    onConvertToProject,
    onClose,
    currentProjectId,
  }: Props = $props();

  let menuEl = $state<HTMLDivElement | undefined>();
  /** The task whose menu currently shows the project search instead of its items. */
  let pickingTaskId = $state<string | null>(null);
  let picking = $derived(!!task && pickingTaskId === task.id);
  let floatingStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');

  const PRIORITIES: { value: TaskPriority; icon: string; label: string }[] = [
    { value: 'none', icon: '—', label: 'No priority' },
    { value: 'low', icon: '🔵', label: 'Low' },
    { value: 'medium', icon: '🟠', label: 'Medium' },
    { value: 'high', icon: '🔴', label: 'High' },
  ];

  async function updatePosition(anchor: HTMLElement, floating: HTMLElement) {
    if (!anchor.isConnected) return;

    const result = await computePosition(anchor, floating, {
      strategy: 'fixed',
      placement: 'bottom-end',
      middleware: [
        offset(4),
        flip({ fallbackPlacements: ['top-end', 'bottom-start', 'top-start'] }),
        shift({ padding: 8 }),
      ],
    });
    floatingStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; visibility: visible;`;
  }

  // Reposition while open — the card can be dragged or the canvas panned underneath.
  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate lifecycle: subscribes on open, returns cleanup
  $effect(() => {
    if (!task || !anchorEl || !menuEl) return;

    const anchor = anchorEl;
    const floating = menuEl;
    return autoUpdate(anchor, floating, () => updatePosition(anchor, floating));
  });

  // Reset visibility between openings so the menu never flashes at a stale spot.
  // eslint-disable-next-line momentium/no-unguarded-effect -- resets positioning state when the anchored task changes
  $effect(() => {
    if (!task) {
      floatingStyle = 'position: fixed; left: 0; top: 0; visibility: hidden;';
      pickingTaskId = null;
    }
  });

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if task}
  <!--
    No click handler needed to keep the menu open: the canvas's outside-click
    dismissal skips anything inside [data-board-task-menu].
  -->
  <div
    bind:this={menuEl}
    class="task-menu"
    class:picking
    style={floatingStyle}
    role="menu"
    tabindex="-1"
    data-board-task-menu
  >
    {#if picking && currentProjectId}
      <MoveToProjectPanel
        excludeProjectId={currentProjectId}
        onSelect={(projectId, projectTitle) => onMoveToProject(task, projectId, projectTitle)}
        onBack={() => (pickingTaskId = null)}
      />
    {:else}
      <div class="menu-label">Priority:</div>
      <div class="priority-row">
        {#each PRIORITIES as option (option.value)}
          <button
            type="button"
            class="priority-option"
            class:active={task.priority === option.value}
            title={option.label}
            aria-label={option.label}
            onclick={() => onSetPriority(task, option.value)}>{option.icon}</button
          >
        {/each}
      </div>

      <div class="menu-divider"></div>

      <button type="button" class="menu-item" onclick={() => onDuplicate(task)}
        ><Icon name="duplicate" />Duplicate task</button
      >
      <TaskCopyActionRow {task} onDone={onClose} />
      <button type="button" class="menu-item" onclick={() => onMarkWontDo(task)}
        ><Icon name="wont-do" />Won't do</button
      >
      <button type="button" class="menu-item menu-item-danger" onclick={() => onDelete(task)}>
        <Icon name="trash" />Delete
      </button>

      <div class="menu-divider"></div>

      {#if currentProjectId}
        <RecentMoveMenuItem
          {currentProjectId}
          onMove={(projectId, projectTitle) => onMoveToProject(task, projectId, projectTitle)}
        />
      {/if}

      <button type="button" class="menu-item" onclick={() => (pickingTaskId = task.id)}>
        <Icon name="move-to-project" />Move to project…
        <Icon name="chevron-right" size={12} class="menu-trail" />
      </button>
      <button type="button" class="menu-item" onclick={() => onConvertToProject(task)}>
        <Icon name="convert" />Convert to project
      </button>
    {/if}
  </div>
{/if}

<style>
  .task-menu {
    min-width: 10rem;
    max-height: 16rem;
    overflow-y: auto;
    padding: 0.125rem 0;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 60;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    padding: 0.375rem 0.5rem;
    font-size: 0.6875rem;
    font-family: inherit;
    color: #374151;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
  }

  .menu-item:hover {
    background: #f3f4f6;
  }

  .menu-item :global(.icon) {
    color: #6b7280;
  }

  .menu-item :global(.menu-trail) {
    margin-left: auto;
    color: #9ca3af;
  }

  .menu-item-danger :global(.icon) {
    color: inherit;
  }

  .task-menu.picking {
    width: 16rem;
    max-height: none;
    padding: 0;
    overflow: hidden;
  }

  .menu-item-danger {
    color: #dc2626;
  }

  .menu-item-danger:hover {
    background: #fef2f2;
  }

  .menu-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 0.125rem 0;
  }

  .menu-label {
    padding: 0.25rem 0.5rem;
    font-size: 0.5625rem;
    color: #9ca3af;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .priority-row {
    display: flex;
    gap: 0.125rem;
    padding: 0.125rem 0.5rem 0.25rem;
  }

  .priority-option {
    width: 1.5rem;
    height: 1.375rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    font-family: inherit;
    background: none;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
  }

  .priority-option:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }

  .priority-option.active {
    background: #eff6ff;
    border-color: #3b82f6;
  }
</style>
